import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/SearchableSelect';
import { INVOICE_TYPES, coreFieldsError } from '@/lib/mandatoryFields';

const lsName = (v) => (v && typeof v === 'object' ? v.name : v) || '';
const audName = (so) => (so.audiologist && typeof so.audiologist === 'object' ? so.audiologist.name : so.audiologist) || so.audiologist_name || '';

// Values already present on the SO (SO always wins at conversion).
export const soCoreValues = (so) => ({
  customer_name: so.customer_name || so.customer?.name || '',
  customer_phone: so.customer_phone || so.customer?.phone || '',
  lead_source: lsName(so.lead_source),
  audiologist_name: audName(so),
  invoice_type: so.invoice_type || '',
});

export const soCoreError = (so, fill) => {
  const v = soCoreValues(so);
  return coreFieldsError({
    name: v.customer_name || fill.customer_name, phone: v.customer_phone || fill.customer_phone,
    leadSource: v.lead_source || fill.lead_source, audiologist: v.audiologist_name || fill.audiologist_name,
    invoiceType: v.invoice_type || fill.invoice_type,
  });
};

const Row = ({ label, value, children, k }) => (
  <div data-testid={`so-core-${k}`}>
    <label className="text-[10px] uppercase tracking-wide text-gray-500 block mb-1">{label} <span className="text-red-500">*</span></label>
    {value ? <p className="h-9 flex items-center px-3 rounded-md bg-gray-50 border text-sm text-[#0B1B3D]" data-testid={`so-core-${k}-value`}>{value}</p> : children}
  </div>
);

// Shows the SO's mandatory details read-only; only missing ones become required inputs.
export function SOCoreFieldsPanel({ so, fill, setFill, loadAudiologists, loadLeadSources }) {
  const v = soCoreValues(so);
  const [auds, setAuds] = useState([]);
  const [sources, setSources] = useState([]);
  useEffect(() => {
    if (!v.audiologist_name) loadAudiologists().then(r => setAuds(r.data.audiologists || [])).catch(() => {});
    if (!v.lead_source) loadLeadSources().then(r => setSources(r.data.lead_sources || [])).catch(() => {});
  }, [so.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, val) => setFill(f => ({ ...f, [k]: val }));
  return (
    <div className="grid grid-cols-2 gap-3 border rounded-xl p-3 bg-white" data-testid="so-core-fields">
      <Row label="Customer Name" value={v.customer_name} k="name">
        <Input className="h-9" value={fill.customer_name || ''} onChange={e => set('customer_name', e.target.value)} data-testid="so-core-name-input" />
      </Row>
      <Row label="Phone" value={v.customer_phone} k="phone">
        <Input className="h-9" value={fill.customer_phone || ''} onChange={e => set('customer_phone', e.target.value)} placeholder="10-digit mobile" data-testid="so-core-phone-input" />
      </Row>
      <Row label="Lead Source" value={v.lead_source} k="lead-source">
        <SearchableSelect value={fill.lead_source || ''} onChange={val => set('lead_source', val)} placeholder="Select lead source"
          options={sources.map(s => ({ value: s.name, label: s.name }))} testid="so-core-lead-source-input" buttonClassName="w-full h-9" />
      </Row>
      <Row label="Audiologist" value={v.audiologist_name} k="audiologist">
        <SearchableSelect value={fill.audiologist_employee_number || ''} placeholder="Select audiologist"
          onChange={val => { const a = auds.find(x => x.employee_number === val); setFill(f => ({ ...f, audiologist_employee_number: val, audiologist_name: a?.full_name || a?.display_name || '' })); }}
          options={auds.map(a => ({ value: a.employee_number, label: a.full_name || a.display_name }))} testid="so-core-audiologist-input" buttonClassName="w-full h-9" />
      </Row>
      <Row label="Invoice Type" value={v.invoice_type} k="invoice-type">
        <SearchableSelect value={fill.invoice_type || ''} onChange={val => set('invoice_type', val)} placeholder="Select invoice type"
          options={INVOICE_TYPES.map(t => ({ value: t, label: t }))} testid="so-core-invoice-type-input" buttonClassName="w-full h-9" />
      </Row>
    </div>
  );
}
